import React, { useEffect, useMemo, useState } from "react";
import { FaTrash, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { IconWrapper } from "./IconWrapper";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../utils/themeUtils";

interface ChannelInput {
  id: string;
  name: string;
}
interface EditProjectFormProps {
  projectName: string;
  setProjectName: (name: string) => void;

  channelInputs: ChannelInput[];
  setChannelInputs: React.Dispatch<React.SetStateAction<ChannelInput[]>>;
  handleUpdateProject: () => void;
  handleCancel: () => void;
  isUpdating: boolean;
  textColor: string;
  cardBg: string;
  isSidebarOpen: boolean;
}

const PAGE_SIZE = 3;

const EditProjectForm: React.FC<EditProjectFormProps> = ({
  projectName,
  setProjectName,
  channelInputs,
  setChannelInputs,
  handleUpdateProject,
  handleCancel,
  isUpdating,
  textColor,
  cardBg,
  isSidebarOpen,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(channelInputs.length / PAGE_SIZE);
  const paginated = channelInputs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // If channels shrink and current page no longer exists, reset to page 1
  useEffect(() => {
    if (currentPage > Math.max(totalPages, 1)) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const textColorClass = useMemo(() => (isDark ? "text-white" : "text-slate-800"), [isDark]);
  const cardBgClass = useMemo(() => (isDark ? "bg-[#1A1C1F]" : "bg-white"), [isDark]);
  const borderClass = useMemo(() => (isDark ? "border-[#2E2F33]" : "border-slate-200"), [isDark]);
  const lightInputClass = "bg-gray-100 text-[#111827] border-[#E5E7EB] placeholder-gray-500 focus:ring-[#B59143] focus:border-[#B59143]";

  return (
    <div className="w-full max-w-4xl">
      <div className="flex flex-col gap-6 mb-4">
        {/* Project Name */}
        <div className={`flex-1 ${cardBgClass} border ${borderClass} rounded-xl p-4 shadow-sm`}>
          <label className={`block text-sm font-semibold mb-2 ${textColorClass}`}>
            Project Name
          </label>
          <input
            className={`${isDark ? theme.searchInput : lightInputClass} w-full rounded-lg px-3 py-2 outline-none ring-0 transition`}
            placeholder="Edit project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
        </div>

        {/* Channels */}
        <div className={`flex-1 ${cardBgClass} border ${borderClass} rounded-xl p-4 shadow-sm`}>
          <div className="flex items-center justify-between">
            <div className={`text-sm font-semibold ${textColorClass}`}>Channels</div>
            <button
              type="button"
              onClick={() => setChannelInputs((prev) => [...prev, { id: "", name: "" }])}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium ${isDark ? theme.goldOutlineButton : theme.goldOutlineButton}`}
            >
              + Add Channel
            </button>
          </div>

          {/* Dynamic Channel Inputs with Pagination */}
          <div className="mt-3 space-y-2">
            {paginated.map((input, idx) => {
              const realIndex = (currentPage - 1) * PAGE_SIZE + idx;
              return (
                <div key={realIndex} className="flex items-center gap-2">
                  <input
                    className={`${isDark ? theme.searchInput : lightInputClass} w-full rounded-lg px-3 py-2 outline-none ring-0 transition`}
                    placeholder="Channel Name"
                    value={input.name}
                    onChange={(e) => {
                      const arr = [...channelInputs];
                      arr[realIndex] = { ...arr[realIndex], name: e.target.value };
                      setChannelInputs(arr);
                    }}
                  />
                  <button
                    type="button"
                    aria-label="Remove channel"
                    onClick={() => {
                      const arr = [...channelInputs];
                      arr.splice(realIndex, 1);
                      setChannelInputs(arr);
                    }}
                    className={`inline-flex items-center justify-center rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-[#C3C9D3] hover:bg-[#2A3441]' : 'border-slate-200 bg-white text-slate-500 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600'} p-2 transition`}
                  >
                    <IconWrapper icon={FaTrash} className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={`mt-4 flex items-center justify-center gap-2 rounded-b-xl ${cardBgClass} py-2`}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white enabled:hover:bg-[#2A3441]' : 'border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 enabled:hover:border-slate-300'} disabled:opacity-40 disabled:cursor-not-allowed transition`}
                aria-label="Previous page"
              >
                <IconWrapper icon={FaArrowLeft} className="h-4 w-4" />
              </button>
              <span className={`px-3 text-sm font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-slate-600'}`}>{`${currentPage} / ${totalPages}`}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border ${isDark ? 'border-[#2E2F33] bg-[#14161B] text-white enabled:hover:bg-[#2A3441]' : 'border-slate-200 bg-white text-slate-600 enabled:hover:bg-slate-50 enabled:hover:border-slate-300'} disabled:opacity-40 disabled:cursor-not-allowed transition`}
                aria-label="Next page"
              >
                <IconWrapper icon={FaArrowRight} className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleCancel}
          className={`flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 focus:outline-none focus:ring-2 transition ${isDark ? 'bg-[#2A3441] text-white hover:bg-[#3A4553] focus:ring-[#B59143]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500'}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleUpdateProject}
          disabled={isUpdating || !projectName.trim()}
          className={`flex-1 inline-flex items-center justify-center rounded-lg px-4 py-2 text-white shadow-md focus:outline-none focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed transition ${theme.primaryButton}`}
        >
          {isUpdating && (
            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a 8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          )}
          {isUpdating ? "Updating..." : "Update"}
        </button>
      </div>
    </div>
  );
};

export default EditProjectForm;
